<template>
  <div data-page-translator-ignore>
    <div
      v-if="visible"
      class="page-translator"
      :class="{ 'is-meeting-detail': isMeetingDetailRoute }"
      :style="wrapperStyle"
    >
      <button
        class="page-translator__trigger"
        type="button"
        title="页面翻译"
        @mousedown.prevent="handleMouseDown"
        @touchstart.stop="handleTouchStart"
        @click.stop="handleIconClick"
      >
        <img
          class="page-translator__icon"
          src="@images/icon/page-translate@2x.png"
          alt="页面翻译"
        />
      </button>

      <div v-show="menuVisible" class="page-translator__menu">
        <button
          v-for="item in langOptions"
          :key="item.value"
          class="page-translator__menu-item"
          :class="{ 'is-active': currentLang === item.value }"
          type="button"
          @click.stop="selectLanguage(item.value)"
        >
          <span>{{ item.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Request from '@utils/request'

const { PageTranslatorEngine } = require('@/utils/pageTranslator/engine')
const {
  getStoredLanguage,
  setStoredLanguage,
  getPositionStorageKey,
  isRouteAllowed,
  resolveMeetingId,
  isExpertTaskRoute,
  getExpertTaskId,
  TRANSLATOR_MEETING_READY_EVENT,
  normalizeTranslateStatusResponse,
  DEFAULT_TRANSLATOR_OPTIONS
} = require('@/utils/pageTranslator/config')
const { normalizeTranslatorLang } = require('@/utils/pageTranslator/rules')

const POSITION_CACHE_VERSION = 3

export default {
  name: 'PageTranslator',
  data () {
    return {
      visible: false,
      menuVisible: false,
      currentLang: 'none',
      routeKey: '',
      meetingId: '',
      engine: null,
      initVersion: 0,
      hasCustomPosition: false,
      position: {
        left: 0,
        top: 0
      },
      dragState: {
        dragging: false,
        moved: false,
        startX: 0,
        startY: 0,
        left: 0,
        top: 0,
        hadCustomPosition: false
      },
      langOptions: [
        { label: '中文（简体）', value: 'none' },
        // { label: '中文（简体）', value: 'zh' },
        { label: 'English', value: 'en' }
      ]
    }
  },
  computed: {
    wrapperStyle () {
      if (!this.hasCustomPosition) return {}
      return {
        left: `${this.position.left}px`,
        top: `${this.position.top}px`,
        right: 'auto'
      }
    },
    isMeetingDetailRoute () {
      return this.$route && (this.$route.name === 'meetingDetail' || /^\/meetingDetail(?:\/|$)/.test(this.$route.path || ''))
    }
  },
  watch: {
    $route () {
      this.initTranslator()
    }
  },
  mounted () {
    this.currentLang = getStoredLanguage(window.localStorage)
    window.addEventListener(TRANSLATOR_MEETING_READY_EVENT, this.handleTranslatorMeetingReady)
    this.initTranslator()
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('mouseup', this.handleMouseUp)
    window.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    window.addEventListener('touchend', this.handleTouchEnd)
    window.addEventListener('touchcancel', this.handleTouchEnd)
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('click', this.closeMenu)
  },
  beforeDestroy () {
    this.destroyEngine()
    window.removeEventListener(TRANSLATOR_MEETING_READY_EVENT, this.handleTranslatorMeetingReady)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseup', this.handleMouseUp)
    window.removeEventListener('touchmove', this.handleTouchMove)
    window.removeEventListener('touchend', this.handleTouchEnd)
    window.removeEventListener('touchcancel', this.handleTouchEnd)
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.closeMenu)
  },
  methods: {
    isDebugEnabled () {
      return this.$route && this.$route.query && this.$route.query.translator_debug == '1'
    },
    debugLog (stage, payload) {
      if (!this.isDebugEnabled()) return
      console.info('[PageTranslator]', stage, payload || '')
    },
    initTranslator () {
      if (typeof window === 'undefined') return

      const version = ++this.initVersion
      this.menuVisible = false
      this.visible = false
      this.destroyEngine()

      this.debugLog('init', {
        routeName: this.$route && this.$route.name,
        path: this.$route && this.$route.path,
        query: this.$route && this.$route.query,
        params: this.$route && this.$route.params
      })

      if (!isRouteAllowed(this.$route)) {
        this.debugLog('route-not-allowed')
        return
      }

      this.routeKey = getPositionStorageKey(this.$route)
      this.position = this.readPosition()

      if (isExpertTaskRoute(this.$route)) {
        this.debugLog('waiting-expert-task-meeting', {
          taskId: getExpertTaskId(this.$route)
        })
        return
      }

      const meetingId = resolveMeetingId(this.$route)
      if (!meetingId) {
        this.debugLog('missing-meeting-id')
        return
      }

      this.meetingId = meetingId
      this.debugLog('request-status', { meetingId })

      this.checkTranslateStatus(meetingId, version)
    },
    handleTranslatorMeetingReady (event) {
      if (!isExpertTaskRoute(this.$route)) return

      const detail = event && event.detail
      const taskId = getExpertTaskId(this.$route)
      if (
        !detail ||
        !detail.meetingId ||
        !taskId ||
        String(detail.taskId) !== String(taskId)
      ) return

      this.meetingId = detail.meetingId
      this.debugLog('expert-task-meeting-ready', detail)
      this.checkTranslateStatus(detail.meetingId, this.initVersion)
    },
    checkTranslateStatus (meetingId, version) {
      this.debugLog('request-status', { meetingId })
      Request.post('/api/web/v3/alimt/getTranslateStatus', { meeting_id: meetingId }, false)
        .then(res => {
          if (version !== this.initVersion) return
          const statusData = normalizeTranslateStatusResponse(res)
          this.debugLog('status-response', statusData)
          if (!statusData || String(statusData.is_translate) !== '1') {
            this.debugLog('status-disabled', statusData)
            return
          }

          this.visible = true
          this.currentLang = getStoredLanguage(window.localStorage)
          this.createEngine()
          this.debugLog('visible', { lang: this.currentLang })
          this.$nextTick(() => {
            if (this.hasCustomPosition) this.position = this.clampPosition(this.position)
            if (this.currentLang !== 'none' && this.engine) this.engine.setLanguage(this.currentLang)
          })
        })
        .catch(error => {
          if (version !== this.initVersion) return
          this.debugLog('status-error', error)
          this.visible = false
        })
    },
    createEngine () {
      this.destroyEngine()
      this.engine = new PageTranslatorEngine({
        ...DEFAULT_TRANSLATOR_OPTIONS,
        translateBatch: this.translateBatch,
        message: msg => {
          if (window.vant && window.vant.Toast) window.vant.Toast(msg)
        }
      })
    },
    destroyEngine () {
      if (!this.engine) return
      this.engine.destroy()
      this.engine = null
    },
    translateBatch (sourceTexts, targetLang) {
      return Request.post('/api/web/v3/alimt/batchTranslate', {
        source_texts: JSON.stringify(sourceTexts),
        target_lang: targetLang
      }, false).then(res => {
        return res && res.data && typeof res.data === 'object' ? res.data : res
      })
    },
    selectLanguage (lang) {
      const nextLang = normalizeTranslatorLang(lang)
      this.currentLang = nextLang
      setStoredLanguage(window.localStorage, nextLang)
      this.menuVisible = false
      if (this.engine) this.engine.setLanguage(nextLang)
    },
    handleIconClick () {
      if (this.dragState.moved) {
        this.dragState.moved = false
        return
      }
      this.menuVisible = !this.menuVisible
    },
    closeMenu (event) {
      if (!this.$el || this.$el.contains(event.target)) return
      this.menuVisible = false
    },
    handleMouseDown (event) {
      this.startDrag(event.clientX, event.clientY)
    },
    handleTouchStart (event) {
      const touch = event.touches && event.touches[0]
      if (!touch) return
      this.startDrag(touch.clientX, touch.clientY)
    },
    startDrag (clientX, clientY) {
      const hadCustomPosition = this.hasCustomPosition
      const currentPosition = this.getCurrentPosition()
      this.hasCustomPosition = true
      this.position = currentPosition
      this.dragState = {
        dragging: true,
        moved: false,
        startX: clientX,
        startY: clientY,
        left: currentPosition.left,
        top: currentPosition.top,
        hadCustomPosition
      }
    },
    handleMouseMove (event) {
      this.moveDrag(event.clientX, event.clientY)
    },
    handleTouchMove (event) {
      if (!this.dragState.dragging) return
      const touch = event.touches && event.touches[0]
      if (!touch) return
      event.preventDefault()
      this.moveDrag(touch.clientX, touch.clientY)
    },
    moveDrag (clientX, clientY) {
      if (!this.dragState.dragging) return
      const diffX = clientX - this.dragState.startX
      const diffY = clientY - this.dragState.startY
      if (Math.abs(diffX) > 3 || Math.abs(diffY) > 3) this.dragState.moved = true
      this.position = this.clampPosition({
        left: this.dragState.left + diffX,
        top: this.dragState.top + diffY
      })
    },
    handleTouchEnd () {
      this.handleMouseUp()
    },
    handleMouseUp () {
      if (!this.dragState.dragging) return
      this.dragState.dragging = false
      if (!this.dragState.moved) {
        if (!this.dragState.hadCustomPosition) {
          this.hasCustomPosition = false
          this.position = { left: 0, top: 0 }
        }
        return
      }
      this.savePosition()
    },
    handleResize () {
      if (!this.hasCustomPosition) return
      this.position = this.clampPosition(this.position)
      this.savePosition()
    },
    readPosition () {
      try {
        const value = window.localStorage.getItem(this.routeKey)
        if (!value) {
          this.hasCustomPosition = false
          return { left: 0, top: 0 }
        }
        const parsed = JSON.parse(value)
        if (parsed.version !== POSITION_CACHE_VERSION) {
          this.hasCustomPosition = false
          return { left: 0, top: 0 }
        }
        this.hasCustomPosition = true
        return this.clampPosition({
          left: Number(parsed.left) || 0,
          top: Number(parsed.top) || 0
        })
      } catch (error) {
        this.hasCustomPosition = false
        return { left: 0, top: 0 }
      }
    },
    savePosition () {
      if (!this.routeKey) return
      window.localStorage.setItem(this.routeKey, JSON.stringify({
        version: POSITION_CACHE_VERSION,
        left: this.position.left,
        top: this.position.top
      }))
    },
    getCurrentPosition () {
      const width = 53
      const height = 53
      const element = this.$el && this.$el.querySelector
        ? this.$el.querySelector('.page-translator')
        : null
      const rect = element ? element.getBoundingClientRect() : null
      if (rect) return this.clampPosition({ left: rect.left, top: rect.top })
      return {
        left: Math.max(12, window.innerWidth - width - 3),
        top: Math.max(12, Math.round(window.innerHeight * 0.7 - height / 2))
      }
    },
    clampPosition (position) {
      const width = 53
      const height = 53
      const maxLeft = Math.max(12, window.innerWidth - width - 12)
      const maxTop = Math.max(12, window.innerHeight - height - 12)
      return {
        left: Math.min(Math.max(12, position.left), maxLeft),
        top: Math.min(Math.max(12, position.top), maxTop)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-translator {
  position: fixed;
  z-index: 3000;
  width: 53px;
  height: 53px;
  top: calc(70vh - 26.5px);
  right: 3px;
  user-select: none;
}

.page-translator.is-meeting-detail {
  top: calc(70vh - 26.5px);
}

.page-translator__trigger {
  position: relative;
  display: block;
  width: 53px;
  height: 53px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  outline: none;
  touch-action: none;
}

.page-translator__icon {
  display: block;
  width: 53px;
  height: 53px;
  pointer-events: none;
}

.page-translator__menu {
  position: absolute;
  top: -4px;
  right: 46px;
  width: 120px;
  padding: 6px;
  border: 0;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(27, 50, 89, 0.18);
}

.page-translator__menu-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 2px;
  background: #ffffff;
  color: #333333;
  font-size: 14px;
  line-height: 28px;
  text-align: left;
  cursor: pointer;
}

.page-translator__menu-item:hover {
  background: #e8f3ff;
  color: #0089ff;
}

.page-translator__menu-item.is-active {
  background: #ddecff;
  color: #0089ff;
}

</style>
